import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Stop,
  PlayArrow as StartIcon,
  Person as BrokerIcon,
} from '@mui/icons-material';
import Media from "../Components/Media/Media";
// import QRCodeGenerator from "../Components/QRCode/QRCodeGenerator";
import { useAuth } from '../contexts/JWTAuthContext';
import {
  startSession,
  endSession,
  getSessionStatus,
  submitSessionFeedback
} from '../Services/sessionService';
import { getBrokers } from '../Services/brokerService';
import SessionFeedbackModal from '../Components/SessionFeedbackModal';
import { getBrowserLocation, sanitizeLocationPayload } from '../utils/location';
import { reverseGeocode } from '../Services/locationService';

const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  background: '#f8f9fa',
  padding: theme.spacing(4, 2),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6, 4),
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  background: 'white',
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(70, 80, 100, 0.1)',
  overflow: 'hidden',
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4),
  },
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  background: '#465064',
  color: 'white',
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  overflow: 'hidden',
  position: 'relative',
}));

const ORIENTATION_LOCATION_LABEL = 'Orientation Center';
const ORIENTATION_LOCATION_NOTES = 'Orientation session';

const QRPage = () => {
  const { user } = useAuth();
  const [sessionState, setSessionState] = useState({
    hasActiveSession: false,
    activeSession: null,
    loading: false
  });

  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });

  const [feedbackModal, setFeedbackModal] = useState({
    open: false,
    sessionData: null,
    loading: false
  });

  // Broker picker state
  const [brokerDialog, setBrokerDialog] = useState({ open: false, loading: false });
  const [brokers, setBrokers] = useState([]);
  const [selectedBrokerId, setSelectedBrokerId] = useState('');

  const [mediaLoading, setMediaLoading] = useState(true); // Start as true, Media will set to false when done



  // Check session status on component mount
  useEffect(() => {
    checkSessionStatus();
  }, []);

  // Fallback timeout: if loading takes too long, show content anyway
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (mediaLoading) {
        console.warn('Loading timeout reached, showing content anyway');
        setMediaLoading(false);
      }
    }, 30000); // 30 seconds timeout

    return () => clearTimeout(timeout);
  }, [mediaLoading]);

  const captureLocationDetails = useCallback(async (label, actionVerb) => {
    const locationResult = await getBrowserLocation({ locationAddress: label });

    if (locationResult.error || typeof locationResult.latitude !== 'number' || typeof locationResult.longitude !== 'number') {
      throw new Error(`Unable to ${actionVerb} the session without location access. Please enable location permissions and try again.`);
    }

    try {
      const resolvedAddress = await reverseGeocode(locationResult.latitude, locationResult.longitude);
      return sanitizeLocationPayload({
        latitude: locationResult.latitude,
        longitude: locationResult.longitude,
        locationAddress: resolvedAddress
      });
    } catch (geocodeError) {
      console.error('Reverse geocoding failed:', geocodeError);
      throw new Error(`Unable to ${actionVerb} the session because we could not determine the location address. Please try again.`);
    }
  }, []);

  const checkSessionStatus = async () => {
    try {
      setSessionState(prev => ({ ...prev, loading: true }));
      const response = await getSessionStatus();
      
      if (response.succeeded) {
        const hasActiveOrientation = response.data.hasActiveOrientation;
        setSessionState({
          hasActiveSession: hasActiveOrientation,
          activeSession: hasActiveOrientation ? response.data.activeOrientationSession : null,
          loading: false
        });
      }
    } catch (error) {
      console.error('Error checking session status:', error);
      setSessionState(prev => ({ ...prev, loading: false }));
    }
  };

  // Step 1: open broker picker
  const handleStartSession = async () => {
    setBrokerDialog({ open: true, loading: true });
    setSelectedBrokerId('');
    try {
      const res = await getBrokers();
      const parsed = typeof res === 'string' ? JSON.parse(res) : res;
      const list = parsed?.data ?? parsed ?? [];
      setBrokers(Array.isArray(list) ? list : []);
    } catch {
      setBrokers([]);
    } finally {
      setBrokerDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Step 2: confirm broker selection then start session
  const handleConfirmBrokerAndStart = async () => {
    setBrokerDialog(prev => ({ ...prev, loading: true }));
    setSessionState(prev => ({ ...prev, loading: true }));
    try {
      const locationDetails = await captureLocationDetails(`${ORIENTATION_LOCATION_LABEL} (Start)`, 'start');
      const locationLabel = locationDetails.locationAddress || ORIENTATION_LOCATION_LABEL;

      const sessionData = {
        sessionType: 'Orientation',
        clientName: 'Orientation',
        clientEmail: '',
        clientPhoneNumber: '',
        location: locationLabel,
        notes: `${ORIENTATION_LOCATION_NOTES} started`,
        brokerId: Number(selectedBrokerId),
        ...locationDetails
      };

      const response = await startSession(sessionData);

      setBrokerDialog({ open: false, loading: false });

      if (response.succeeded) {
        setSessionState({ hasActiveSession: true, activeSession: response.data, loading: false });
        setSnackbar({ open: true, message: 'Orientation session started successfully!', severity: 'success' });
      } else {
        setSessionState(prev => ({ ...prev, loading: false }));
        setSnackbar({ open: true, message: response.message || 'Failed to start session', severity: 'error' });
      }
    } catch (error) {
      console.error('Error starting session:', error);
      setBrokerDialog({ open: false, loading: false });
      setSessionState(prev => ({ ...prev, loading: false }));
      setSnackbar({ open: true, message: error.message || 'Failed to start session', severity: 'error' });
    }
  };

  const handleEndSession = async () => {
    try {
      setSessionState(prev => ({ ...prev, loading: true }));

      if (!sessionState.activeSession?.id) {
        throw new Error('No active orientation session to end.');
      }

      const locationDetails = await captureLocationDetails(`${ORIENTATION_LOCATION_LABEL} (End)`, 'end');
      
      const response = await endSession(
        sessionState.activeSession.id,
        'Session ended from orientation page',
        locationDetails
      );
      
      if (response.succeeded) {
        const locationLabel = locationDetails.locationAddress || sessionState.activeSession?.locationAddress || ORIENTATION_LOCATION_LABEL;
        // Show feedback modal before ending session
        setFeedbackModal({
          open: true,
          sessionData: {
            ...sessionState.activeSession,
            ...locationDetails,
            locationAddress: locationLabel,
            endTime: new Date().toISOString(),
            duration: response.data?.duration || 'Completed'
          },
          loading: false
        });
        
        setSessionState({
          hasActiveSession: false,
          activeSession: null,
          loading: false
        });
      } else {
        setSessionState(prev => ({ ...prev, loading: false }));
        setSnackbar({
          open: true,
          message: response.message || 'Failed to end session',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error ending session:', error);
      setSessionState(prev => ({ ...prev, loading: false }));
      setSnackbar({
        open: true,
        message: error.message || 'Failed to end session',
        severity: 'error'
      });
    }
  };

  const handleFeedbackSubmit = async (feedbackData) => {
    console.log("feedbackData");
    try {
      console.log("feedbackData", feedbackData);
      setFeedbackModal(prev => ({ ...prev, loading: true }));
      
      const response = await submitSessionFeedback(
        feedbackModal.sessionData.id, 
        feedbackData
      );
      
      if (response.succeeded) {
        setFeedbackModal({ open: false, sessionData: null, loading: false });
        setSnackbar({
          open: true,
          message: 'Session ended and feedback submitted successfully!',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setFeedbackModal(prev => ({ ...prev, loading: false }));
      setSnackbar({
        open: true,
        message: error.message || 'Failed to submit feedback',
        severity: 'error'
      });
    }
  };




  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const resolvedSalesId = useMemo(() => (
    sessionState.activeSession?.salesUserId ??
    sessionState.activeSession?.salesId ??
    sessionState.activeSession?.userId ??
    user?.UserId ??
    user?.id ??
    ""
  ), [sessionState.activeSession, user]);

  const resolvedSessionId = useMemo(() => (
    sessionState.activeSession?.id ??
    sessionState.activeSession?.sessionId ??
    ""
  ), [sessionState.activeSession]);

  return (
    <StyledContainer maxWidth="xl">
      {/* Loading overlay - show until units and first images are ready */}
      {mediaLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.95)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            gap: 2
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ color: '#465064' }}>
            Loading units and images...
          </Typography>
        </Box>
      )}
      <StyledPaper elevation={0}>
        {/* Enhanced Header */}
        <HeaderCard>
          <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
            <Grid container alignItems="center" spacing={3}>
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}>
                    <Typography variant="h2" sx={{ color: 'white' }}>
                      📱
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '1.8rem', md: '2.2rem' },
                        mb: 1
                      }}
                    >
                      Orientation Center
                    </Typography>
                    {sessionState.hasActiveSession && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip 
                            label="Session Active" 
                            size="small" 
                            sx={{ 
                              background: 'rgba(255, 255, 255, 0.2)', 
                              color: 'white',
                              fontWeight: 500
                            }} 
                          />
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            Client: {sessionState.activeSession?.clientName}
                          </Typography>
                        </Box>
                        {sessionState.activeSession?.locationAddress && (
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            Location: {sessionState.activeSession.locationAddress}
                            {typeof sessionState.activeSession.latitude === 'number' && typeof sessionState.activeSession.longitude === 'number'
                              ? ` (${sessionState.activeSession.latitude.toFixed(4)}, ${sessionState.activeSession.longitude.toFixed(4)})`
                              : ''}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 2,
                  alignItems: { xs: 'stretch', md: 'flex-end' }
                }}>
                  {sessionState.loading ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : sessionState.hasActiveSession ? (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<Stop />}
                        onClick={handleEndSession}
                        sx={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          fontWeight: 500,
                          '&:hover': {
                            background: 'rgba(255, 255, 255, 0.3)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        End Session
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<StartIcon />}
                      onClick={handleStartSession}
                      disabled={sessionState.loading}
                      sx={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        fontWeight: 500,
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:disabled': {
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: 'rgba(255, 255, 255, 0.5)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {sessionState.loading ? 'Starting...' : 'Start Session'}
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </HeaderCard>
        <Box sx={{ 
          background: '#f8f9fa',
          borderRadius: 2,
          p: 3,
          border: '1px solid #e9ecef',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: '#465064',
          }
        }}>
          <Media
            showQRCode={sessionState.hasActiveSession}
            salesId={resolvedSalesId}
            sessionId={resolvedSessionId}
            view={!sessionState.hasActiveSession}
            useSameTabForUnitPayment
            onLoadingChange={setMediaLoading}
          />
        </Box>

        {/* Footer Section */}
        <Box sx={{ 
          mt: 3, 
          pt: 3, 
          borderTop: '1px solid #e9ecef',
          textAlign: 'center'
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#6c757d',
              fontWeight: 400,
              opacity: 0.7,
              mb: 2
            }}
          >
            Need help? Contact our support team for assistance with orientation materials
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Chip label="24/7 Support" size="small" sx={{ background: 'rgba(70, 80, 100, 0.1)', color: '#465064' }} />
            <Chip label="Live Chat" size="small" sx={{ background: 'rgba(70, 80, 100, 0.1)', color: '#465064' }} />
            <Chip label="Email Support" size="small" sx={{ background: 'rgba(70, 80, 100, 0.1)', color: '#465064' }} />
          </Box>
        </Box>
      </StyledPaper>



      {/* Broker picker dialog */}
      <Dialog open={brokerDialog.open} onClose={() => !brokerDialog.loading && setBrokerDialog({ open: false, loading: false })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BrokerIcon sx={{ color: '#465064' }} />
          Select Broker
        </DialogTitle>
        <DialogContent>
          {brokerDialog.loading && brokers.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : brokers.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#6c757d', py: 2 }}>
              No brokers are assigned to your account. Please contact your administrator.
            </Typography>
          ) : (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel id="broker-select-label">Broker</InputLabel>
              <Select
                labelId="broker-select-label"
                value={selectedBrokerId}
                label="Broker"
                onChange={(e) => setSelectedBrokerId(e.target.value)}
              >
                {brokers.map((broker) => (
                  <MenuItem key={broker.Id} value={broker.Id}>
                    {broker.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setBrokerDialog({ open: false, loading: false })}
            disabled={brokerDialog.loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmBrokerAndStart}
            disabled={brokerDialog.loading || !selectedBrokerId}
            startIcon={brokerDialog.loading ? <CircularProgress size={16} color="inherit" /> : <StartIcon />}
            sx={{ background: '#465064', '&:hover': { background: '#3a4254' } }}
          >
            {brokerDialog.loading ? 'Starting...' : 'Start Session'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Session feedback modal */}
      <SessionFeedbackModal
        open={feedbackModal.open}
        sessionData={feedbackModal.sessionData}
        loading={feedbackModal.loading}
        onSubmit={handleFeedbackSubmit}
        onClose={() => setFeedbackModal({ open: false, sessionData: null, loading: false })}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: 2,
            fontWeight: 600
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </StyledContainer>
  );
};

export default QRPage;
